import { inject, Injectable, Signal } from "@angular/core";
import * as signalR from '@microsoft/signalr';
import { HttpTransportType } from '@microsoft/signalr';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { AuthStore, IAuthAccessToken } from '@auth/data-access';
import { EClientNotificationsEventTypes } from '@shared/push-notification';
import { DateTimeService } from "@application/data-access";

@Injectable({
  providedIn: 'root'
})
export class EventProgressService {
  private readonly authStore = inject(AuthStore);
  private readonly authData: Signal<IAuthAccessToken> = this.authStore.authData;
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;
  private readonly dateTimeService = inject(DateTimeService);

  public listenCrmProgress(): Observable<{ eventName: string; eventData: any }> {
    return this.listenHub(
      this.apiUrl.crm_service,
      [EClientNotificationsEventTypes.onCrmEventProgressNotification],
      (eventData) => this.dateTimeService.toLocalObject(eventData),
    );
  }

  public listenContractProgress(): Observable<{ eventName: string; eventData: any }> {
    return this.listenHub(this.apiUrl.contract, [
      EClientNotificationsEventTypes.onContractNotSentEventProgressNotification,
      EClientNotificationsEventTypes.onContractPendingEventProgressNotification,
    ]);
  }

  /**
   * Opens a SignalR connection to the given hub and emits a normalized
   * `{ eventName, eventData }` for each of the listened event names. `mapEventData`
   * lets a caller post-process the payload (e.g. CRM converts date fields to local time).
   */
  private listenHub(
    baseUrl: string,
    eventNames: EClientNotificationsEventTypes[],
    mapEventData: (eventData: any) => any = (eventData) => eventData,
  ): Observable<{ eventName: string; eventData: any }> {
    const personId = this.authData()?.PersonId;

    return new Observable((obs) => {
      // Without an authenticated person there is nothing to listen to and the
      // hub URL would carry `PersonId=undefined`; skip opening the socket.
      if (!personId) {
        obs.complete();
        return () => undefined;
      }

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(
          baseUrl + `/NotificationHub?PersonId=${personId}`,
          { skipNegotiation: true, transport: HttpTransportType.WebSockets }
        )
        .withAutomaticReconnect([5000, 5000, 5000, 10000])
        .build();

      eventNames.forEach((eventName) => {
        connection.on(eventName, (data) => {
          const eventData: any = {};
          for (const [key, value] of Object.entries(data ?? {})) {
            eventData[this.turnPascalCase(key)] = value;
          }
          obs.next({ eventName, eventData: mapEventData(eventData) });
        });
      });

      let disposed = false;
      const startPromise = connection.start();
      startPromise.catch((error) => {
        // A rejection caused by teardown (the socket being stopped mid-handshake)
        // is expected noise, not a real failure — only surface genuine errors.
        if (!disposed) {
          obs.error(error);
        }
      });

      return () => {
        disposed = true;
        // Wait for the handshake to settle before stopping. Calling `stop()`
        // while the connection is still `Connecting` cancels the handshake and
        // makes SignalR log "Handshake was canceled".
        startPromise
          .then(() => connection.stop())
          .catch(() => undefined);
      };
    });
  }

  private turnPascalCase(key: string): string {
    return `${key[0].toUpperCase()}${key.substring(1, key.length)}`;
  }
}
